import React from "react";
import { Divider } from "@mui/material";
import { connect } from "react-redux";
import styles from "./RequirementMatrix.module.css";
import { ApiService, CategoryService, RequirementMatrixService } from "../../../api";
import { Flags, isSegFeatureEnabled, SegFlags, translate, translateDate } from "../../../common/providers";
import { isDeepEqual, updateTreeBranch } from "../../../common/utils";
import { EditReqDialog, ReqDetails, ReqFilters, ReqList } from "./components";
import { exportView } from "../../../navigation";
import {
	CustomButton,
	generateFilters,
	icon,
	ViewTabs,
	withWebsocket,
	IconComponent,
	CustomTooltip,
	AiAgentConfigDialog,
	withtrackEvent,
} from "../../../common/components";
import { ExportDialog } from "../coverage/components";
import { SEPARATOR } from "../../smart-search/utils/utils";
import { ACTION_OPTION, DOCUMENT_OPTION } from "../../../common/constants";

const mapStateToProps = ({ context }) => ({
	project: context.project,
	hasAvailableAIQuota: context.hasAvailableAIQuota,
});

class RequirementMatrix extends React.Component {
	constructor(props) {
		super(props);
		const updateCancelTokenSources = [
			ApiService.getCancelTokenSource(),
			ApiService.getCancelTokenSource(),
			ApiService.getCancelTokenSource(),
			ApiService.getCancelTokenSource(),
		];
		this.state = {
			totalRequirementsInProject: 0,
			totalDeletedRequirementsInProject: 0,
			currentPage: 0,
			requirements: {}, // Page + Contents(requirements)
			filters: { separator: SEPARATOR.AND },
			categories: null,
			parameters: null,
			selectedRequirement: null,
			isAgentInProgress: false,
			isLoadingReqs: false,
			isLoadingDetails: false,
			openEditRedDialog: false,
			openAiSidePanel: false,
			updateCancelTokenSources,
			openExport: false,
			selectedTab: "active",
			isActiveFilter: false,
			isGenerating: false,
			lastExecution: {},
		};
		this.cancelTokenSource = ApiService.getCancelTokenSource();
	}

	docOptions = [
		{
			value: DOCUMENT_OPTION.AI,
			label: translate("common:component.ai.document-choice-for-analysis.ai-option"),
		},
		{
			value: DOCUMENT_OPTION.MANUAL,
			label: translate("common:component.ai.document-choice-for-analysis.manual-option"),
		},
	];

	actionOptions = [
		{
			value: ACTION_OPTION.REPLACE,
			label: translate("common:component.ai-implicit-req.detected-content-integration.answer.replace"),
		},
		{
			label: translate("common:component.ai-implicit-req.detected-content-integration.answer.add"),
			value: ACTION_OPTION.APPEND,
		},
	];

	promptModels = [
		{
			title: translate("requirement-matrix.ai-config-dialog.prompt-models.technical-requirements.title"),
			subTitle: translate("requirement-matrix.ai-config-dialog.prompt-models.technical-requirements.subTitle"),
			value: translate("requirement-matrix.ai-config-dialog.prompt-models.technical-requirements.value"),
		},
		{
			title: translate("requirement-matrix.ai-config-dialog.prompt-models.IEE29148.title"),
			subTitle: translate("requirement-matrix.ai-config-dialog.prompt-models.IEE29148.subTitle"),
			value: translate("requirement-matrix.ai-config-dialog.prompt-models.IEE29148.value"),
		},
		{
			title: translate("common:ai-config-dialog.categorization.prompt-models.empty.title"),
			subTitle: translate("common:ai-config-dialog.categorization.prompt-models.empty.subTitle"),
			value: "",
		},
	];

	componentDidMount() {
		const documentTitle = translate("requirement-matrix.document.title");
		document.title = documentTitle;
		this.getFirstRequirements();
		this.getParametersForInformation();
		this.getCategoriesInProject();
		this.subscribeSocket();
		this.getLatestExecution();
	}

	componentWillUnmount() {
		const { updateCancelTokenSources } = this.state;
		updateCancelTokenSources.forEach((tokenSource) => {
			ApiService.cancelTokens(tokenSource);
		});
		ApiService.cancelTokens(this.cancelTokenSource);
	}

	componentDidUpdate(_, prevState) {
		const { selectedRequirement, filters, updateCancelTokenSources, selectedTab } = this.state;
		if (
			(!prevState.selectedRequirement && !!selectedRequirement) ||
			(selectedRequirement && prevState.selectedRequirement.id !== selectedRequirement.id)
		) {
			ApiService.cancelTokens(updateCancelTokenSources[0]);
			updateCancelTokenSources[0] = ApiService.getCancelTokenSource();
			this.getRequirementDetails(updateCancelTokenSources[0]);
		}
		if (
			(!!prevState.filters && filters && !isDeepEqual(prevState.filters, filters)) ||
			prevState.selectedTab !== selectedTab
		) {
			ApiService.cancelTokens(updateCancelTokenSources[1]);
			updateCancelTokenSources[1] = ApiService.getCancelTokenSource();
			this.getRequirements(updateCancelTokenSources[1]);
		}
	}

	getLatestExecution = () => {
		if (isSegFeatureEnabled(SegFlags.AI_IMPLICIT_REQUIREMENT_DETECTOR)) {
			const { project } = this.props;
			RequirementMatrixService.getLastAiAgentExecution({ projectId: project.id }, this.cancelTokenSource.token)
				.then((data) => this.setState({ lastExecution: data }))
				.catch(console.error);
		}
	};

	getFirstRequirements = () => {
		const { project } = this.props;
		const { filters } = this.state;
		this.setState({ isLoadingReqs: true });
		RequirementMatrixService.searchRequirementsInProject(
			{ projectId: project.id },
			{},
			{},
			this.cancelTokenSource.token
		)
			.then((data) =>
				this.setState({
					requirements: data,
					isLoadingReqs: false,
					currentPage: 0,
					totalRequirementsInProject: data.totalElements,
				})
			)
			.catch((err) => {
				console.error(err);
				this.setState({ isLoadingReqs: false });
			});
		RequirementMatrixService.searchRequirementsInProject(
			{ projectId: project.id },
			generateFilters({ ...filters, infoversion: ["DELETED"] }),
			{ page: 0, limit: 1 },
			this.cancelTokenSource.token
		)
			.then((data) => this.setState({ totalDeletedRequirementsInProject: data.totalElements }))
			.catch(console.error);
	};

	getRequirements = (cancelTokenSource) => {
		const { filters, selectedTab } = this.state;
		const { project } = this.props;
		this.setState({ isLoadingReqs: true });
		RequirementMatrixService.searchRequirementsInProject(
			{ projectId: project.id },
			generateFilters(selectedTab === "active" ? filters : { ...filters, infoversion: ["DELETED"] }),
			{},
			cancelTokenSource.token
		)
			.then((data) => {
				this.setState({
					requirements: data,
					selectedRequirement: null,
					currentPage: 0,
					isLoadingReqs: false,
				});
			})
			.catch((err) => {
				console.error(err);
				this.setState({ isLoadingReqs: false });
			});
	};

	getRequirementDetails = (cancelTokenSource) => {
		const { selectedRequirement } = this.state;
		if (!selectedRequirement || !selectedRequirement.id) {
			return;
		}
		this.setState({ isLoadingDetails: true });
		const reqId = selectedRequirement.id;
		if (isSegFeatureEnabled(SegFlags.PARTNERS)) {
			RequirementMatrixService.getRequirementDetails({ reqId }, cancelTokenSource.token)
				.then((data) => {
					this.setState({ isLoadingDetails: false, selectedRequirement: data });
				})
				.catch((err) => this.handleErrorWhileDetailsLoading(err));
		} else {
			RequirementMatrixService.getRequirementDetailsMyCompany({ reqId }, cancelTokenSource.token)
				.then((data) => {
					this.setState({ isLoadingDetails: false, selectedRequirement: data });
				})
				.catch((err) => this.handleErrorWhileDetailsLoading(err));
		}
	};

	getCategoriesInProject = () => {
		const { categories } = this.state;
		const { project } = this.props;
		if (categories) {
			return;
		}
		CategoryService.getTreeParentsForProject(
			{ projectId: project.id, details: false },
			this.cancelTokenSource.token
		)
			.then((data) =>
				this.setState({
					categories: data,
				})
			)
			.catch((err) => {
				console.error(err);
			});
	};

	getParametersForInformation = () => {
		const { parameters } = this.state;
		if (parameters) {
			return;
		}
		RequirementMatrixService.getParametersForInformation(this.cancelTokenSource.token)
			.then((data) => this.setState({ parameters: data }))
			.catch((err) => {
				console.error(err);
			});
	};

	updateRequirement = (payload) => {
		const { selectedRequirement } = this.state;
		if (!selectedRequirement || !selectedRequirement.id) {
			return;
		}
		const reqId = selectedRequirement.id;
		this.setState({ isLoadingDetails: true });
		RequirementMatrixService.updateRequirement({ reqId }, payload, this.cancelTokenSource.token)
			.then((data) => this.setState({ isLoadingDetails: false, selectedRequirement: data }))
			.catch((err) => this.handleErrorWhileDetailsLoading(err));
	};

	handleErrorWhileDetailsLoading = (err) => {
		console.error(err);
		this.setState({ isLoadingDetails: false });
	};

	handleLoadMore = () => {
		const { currentPage, requirements, filters, selectedTab } = this.state;
		const { project } = this.props;
		if (currentPage < requirements.totalPages - 1) {
			const page = currentPage + 1;
			this.setState({ isLoadingReqs: true, currentPage: page });
			return RequirementMatrixService.searchRequirementsInProject(
				{ projectId: project.id },
				generateFilters(selectedTab === "active" ? filters : { ...filters, infoversion: ["DELETED"] }),
				{ page },
				this.cancelTokenSource.token
			)
				.then((data) =>
					this.setState((prev) => ({
						requirements: { ...data, contents: [...prev.requirements.contents, ...data.contents] },
						isLoadingReqs: false,
					}))
				)
				.catch((err) => {
					this.setState({ isLoadingReqs: false });
					console.error(err);
				});
		}
		return null;
	};

	handleSelectRequirement = (id) => {
		const { requirements } = this.state;
		if (!id) {
			return;
		}
		if (!requirements) {
			this.setState({ selectedRequirement: null });
			return;
		}
		const requirement = requirements.contents.find((req) => req.id === id);
		const { selectedRequirement } = this.state;
		if (!requirement || (selectedRequirement && requirement.id === selectedRequirement.id)) {
			return;
		}
		this.setState({ selectedRequirement: requirement });
	};

	handleOpenEditReqDialog = () => {
		const { selectedRequirement } = this.state;
		if (!selectedRequirement) {
			return;
		}
		this.setState({ openEditRedDialog: true });
	};

	handleCloseEditReqDialog = () => {
		this.setState({ openEditRedDialog: false });
	};

	handleApplyFilters = (newFilters) => {
		const initialFilter = { separator: SEPARATOR.AND };
		const isActiveFilter = !isDeepEqual(initialFilter, newFilters);
		this.setState({
			filters: newFilters,
			isActiveFilter,
		});
	};

	handleExpandCategory = (category) => {
		if (!Array.isArray(category.subThematic) || category.subThematic.length === 0) {
			CategoryService.getTreeChildrenForProject(
				{ parentId: category.id, details: false },
				this.cancelTokenSource.token
			)
				.then((data) => {
					this.setState((prev) => ({
						categories: updateTreeBranch({
							tree: prev.categories,
							idToReplace: category.id,
							newBranch: {
								...category,
								subThematic: data,
							},
							iterativeBranchName: "subThematic",
						}),
					}));
				})
				.catch((err) => console.error(err));
		}
	};

	getImplicitReqAgentConfig = (params) =>
		RequirementMatrixService.getImplicitReqAgentConfig(params, this.cancelTokenSource.token);

	getImplicitReqAgentHistory = (params) =>
		RequirementMatrixService.getImplicitReqAgentHistory(params, this.cancelTokenSource.token);

	handleSaveImplicitReqDetection = (
		// eslint-disable-next-line no-unused-vars
		{ _ },
		{ integrationPolicy, documents, additionPrompt, selectDocumentAutomatically }
	) => {
		const { project, trackEvent } = this.props;
		RequirementMatrixService.saveImplicitReqAgentConfig(
			{ projectId: project.id },
			{
				integrationPolicy,
				documents,
				additionPrompt,
				selectDocumentAutomatically,
			},
			this.cancelTokenSource.token
		)
			.catch(console.error)
			.finally(() => {
				trackEvent("ProjectRequirements_Implicit_Requirements_AI_saves_Template");
				this.handleCloseAiSidePanel();
			});
	};

	handleLaunchImplicitReqDetection = (
		// eslint-disable-next-line no-unused-vars
		{ _ },
		{ integrationPolicy, documents, additionPrompt, selectDocumentAutomatically }
	) => {
		const { project, trackEvent } = this.props;
		RequirementMatrixService.saveImplicitReqAgentConfig(
			{ projectId: project.id },
			{
				integrationPolicy,
				documents,
				additionPrompt,
				selectDocumentAutomatically,
			},
			this.cancelTokenSource.token
		).then(() => {
			RequirementMatrixService.launchImplicitReqAgent(
				{ projectId: project.id },
				this.cancelTokenSource.token
			).then(() => {
				this.handleCloseAiSidePanel();
				this.setState({ isAgentInProgress: true, isGenerating: true });
				trackEvent("ProjectRequirements_Implicit_Requirements_AI_Execution");
			});
		});
	};

	handleOpenExportDialog = () => this.setState((prevState) => ({ openExport: !prevState.openExport }));

	handleChangeTab = (_, tab) => {
		this.setState({ selectedTab: tab });
	};

	handleOpenAiSidePanel = () => {
		this.setState({ openAiSidePanel: true });
	};

	handleCloseAiSidePanel = () => {
		this.setState({ openAiSidePanel: false });
	};

	handleRollbackImplicitReqAgentResults = ({ agentExecutionId }) => {
		const { updateCancelTokenSources } = this.state;
		const { project } = this.props;
		RequirementMatrixService.rollbackImplicitReqAgentResults(
			{ projectId: project.id, agentExecutionId },
			this.cancelTokenSource.token
		)
			.then(() => {
				this.handleCloseAiSidePanel();
				ApiService.cancelTokens(updateCancelTokenSources[2]);
				updateCancelTokenSources[2] = ApiService.getCancelTokenSource();
				this.getRequirements(updateCancelTokenSources[2]);
			})
			.catch(console.error);
	};

	handleAutomationMessage = ({ reason, featureName }) => {
		const { updateCancelTokenSources } = this.state;
		if (featureName === "IMPLICIT_REQUIREMENT_DETECTOR") {
			if (reason === "COMPLETED_SUCCESS") {
				ApiService.cancelTokens(updateCancelTokenSources[3]);
				updateCancelTokenSources[3] = ApiService.getCancelTokenSource();
				this.getRequirements(updateCancelTokenSources[3]);
				this.setState({ isAgentInProgress: false, isGenerating: false });
			}
			if (reason === "COMPLETED_FAILURE") {
				this.setState({ isAgentInProgress: false, isGenerating: false });
			}
			this.getLatestExecution();
		}
	};

	subscribeSocket = () => {
		const { subscribeWS, project } = this.props;
		subscribeWS(`/topic/ai-agent/projects/${project.id}/refresh`, this.handleAutomationMessage);
	};

	render() {
		const {
			categories,
			parameters,
			requirements,
			isLoadingReqs,
			selectedRequirement,
			totalRequirementsInProject,
			isAgentInProgress,
			isLoadingDetails,
			openEditRedDialog,
			openExport,
			filters,
			selectedTab,
			openAiSidePanel,
			totalDeletedRequirementsInProject,
			isActiveFilter,
			isGenerating,
			lastExecution,
		} = this.state;
		const { hasAvailableAIQuota } = this.props;
		return (
			<>
				{isGenerating && (
					<div className={styles.isGeneratingContainer}>
						<div>
							<IconComponent spin color="var(--blue-500)" icon={icon.faSpinner} />
						</div>
						<div>
							<div className={styles.isGeneratingContainer__title}>
								{translate("requirement-matrix.is-generating-banner.title")}
							</div>
							<div className={styles.isGeneratingContainer__helper}>
								{translate("requirement-matrix.is-generating-banner.helper")}
							</div>
						</div>
					</div>
				)}
				<ViewTabs
					className={styles.tabs}
					selectedTab={selectedTab}
					tabIdentifier="id"
					tabs={[
						{
							id: "active",
							label: translate("requirement-matrix-tabs-active-req"),
						},
						{
							id: "deleted",
							label: translate("requirement-matrix-tabs-deleted-req"),
						},
					]}
					onChange={this.handleChangeTab}
				/>
				<div className={styles.headerContainer}>
					<ReqFilters defaultFilters={filters} selectedTab={selectedTab} onApply={this.handleApplyFilters} />
					<div className={styles.headerButton__container}>
						{isSegFeatureEnabled(SegFlags.AI_IMPLICIT_REQUIREMENT_DETECTOR) && (
							<CustomTooltip
								title={
									!hasAvailableAIQuota
										? translate("common:company.quotas.consumption-reached.tooltip")
										: (lastExecution?.agentExecutionTime && (
												<>
													{translate(
														"common:component.ai-agent-config.dialog.last-detection"
													)}
													&nbsp;
													{translateDate(lastExecution?.agentExecutionTime)}
													<br />
													{translate("common:header.filter.status")}:&nbsp;
													{lastExecution?.agentStatus}
												</>
										  )) ||
										  ""
								}
							>
								<span>
									<CustomButton
										className={styles.uploadButton}
										color="secondary"
										disabled={isGenerating || !hasAvailableAIQuota}
										size="md"
										startIcon={icon.faWandMagicSparkles}
										variant="outlined"
										onClick={this.handleOpenAiSidePanel}
									>
										{translate("requirement-matrix-actions.detect-implicit-req")}
									</CustomButton>
								</span>
							</CustomTooltip>
						)}
						<CustomButton
							className={styles.uploadButton}
							color="primary"
							startIcon={icon.faUpload}
							variant="contained"
							onClick={this.handleOpenExportDialog}
						>
							{translate("common:btn.export")}
						</CustomButton>
					</div>
				</div>
				<div className={styles.container}>
					<ReqList
						hasMore={requirements.hasMore}
						isActiveFilter={isActiveFilter}
						isAgentInProgress={isAgentInProgress}
						isLoadingReqs={isLoadingReqs}
						requirements={requirements.contents}
						selectedRequirement={selectedRequirement}
						selectedTab={selectedTab}
						totalDeletedRequirementsInProject={totalDeletedRequirementsInProject}
						totalElements={requirements.totalElements}
						totalRequirementsInProject={totalRequirementsInProject}
						onLoadMore={this.handleLoadMore}
						onSelectRequirement={this.handleSelectRequirement}
					/>
					<Divider orientation="vertical" />
					<ReqDetails
						isLoadingDetails={isLoadingDetails}
						requirement={selectedRequirement}
						onClickEdit={this.handleOpenEditReqDialog}
					/>
				</div>
				<EditReqDialog
					categories={categories}
					open={openEditRedDialog}
					params={parameters}
					requirement={selectedRequirement}
					onClose={this.handleCloseEditReqDialog}
					onExpand={this.handleExpandCategory}
					onUpdate={this.updateRequirement}
				/>
				<AiAgentConfigDialog
					actionOptions={this.actionOptions}
					actionOptionsTitle={translate("requirement-matrix.ai-config-dialog.action-options-title")}
					documentOptions={this.docOptions}
					documentOptionsTitle={translate("requirement-matrix.ai-config-dialog.document-options-title")}
					helperText={translate("requirement-matrix.ai-config-dialog.helper-text")}
					historyHeaderTitleKey="requirement-matrix.ai-config-dialog.history-row.title"
					launchTitle={translate("requirement-matrix.ai-config-dialog.launch.btn")}
					open={openAiSidePanel}
					promptHoverText={translate("requirement-matrix.ai-config-dialog.prompt.hover")}
					promptModelHelperText={translate("requirement-matrix.ai-config-dialog.prompt-models.helper-text")}
					promptModels={this.promptModels}
					promptPlaceholder={translate("requirement-matrix.ai-config-dialog.prompt.placeholder")}
					promptTitle={translate("requirement-matrix.ai-config-dialog.prompt.title")}
					templateSelectEventMessage="ProjectRequirements_Implicit_Requirements_AI_applies_template"
					title={translate("common:component.ai-implicit-requirements.dialog.title")}
					onClose={this.handleCloseAiSidePanel}
					onGetHistory={this.getImplicitReqAgentHistory}
					onGetInformation={this.getImplicitReqAgentConfig}
					onLaunch={this.handleLaunchImplicitReqDetection}
					onRollbackAgentResults={this.handleRollbackImplicitReqAgentResults}
					onSave={this.handleSaveImplicitReqDetection}
				/>
				<ExportDialog filters={filters} open={openExport} onClose={this.handleOpenExportDialog} />
			</>
		);
	}
}

export default exportView({
	path: "/projects/:projectId/review/project-requirements",
	localesPath: "/produce/requirement-matrix/locales",
	component: withWebsocket(withtrackEvent(connect(mapStateToProps)(RequirementMatrix))),
	flag: Flags.REQUIREMENT_MATRIX,
	segFlag: SegFlags.PROJECT_REQUIREMENTS,
});
